import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { entitiesApi } from "@/api/counterparties";
import type {
  CreateEntityRequest,
  DedupCandidateDto,
  DedupCheckResultDto,
  EntityDetailDto,
  UpdateEntityRequest,
} from "@/types/counterparties";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import {
  EntityForm,
  EMPTY_ENTITY_FORM,
  type EntityFormState,
} from "@/components/counterparties/EntityForm";
import { DedupCheckModal } from "@/components/counterparties/DedupCheckModal";
import {
  CriticalFieldEditConfirmModal,
  type CriticalFieldChange,
} from "@/components/counterparties/CriticalFieldEditConfirmModal";
import { entityKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

export function EntityEditPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState<EntityFormState>(EMPTY_ENTITY_FORM);
  const [editingEin, setEditingEin] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupResult, setDedupResult] = useState<DedupCheckResultDto | null>(null);
  const [confirmCritical, setConfirmCritical] = useState<{
    changes: CriticalFieldChange[];
    onConfirm: () => void;
  } | null>(null);

  const existing = useQuery({
    queryKey: entityKeys.detail(id),
    queryFn: () => entitiesApi.get(id!),
    enabled: editing,
  });

  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Entities", href: "/counterparties/entities" },
            { label: existing.data.legalEntityName, href: `/counterparties/entities/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Entities", href: "/counterparties/entities" },
          { label: "New entity" },
        ],
  );

  useEffect(() => {
    if (existing.data) {
      setForm(entityDetailToFormState(existing.data));
      setEditingEin(false);
      setEditingBank(false);
    }
  }, [existing.data]);

  const create = useMutation({
    mutationFn: (body: CreateEntityRequest) => entitiesApi.create(body),
    onSuccess: (created) => {
      invalidateCounterpartyReads(queryClient, "entity");
      toast.success(`Entity ${created.legalEntityName} created.`);
      navigate(`/counterparties/entities/${created.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (body: UpdateEntityRequest) => entitiesApi.update(id!, body),
    onSuccess: (updated) => {
      invalidateCounterpartyReads(queryClient, "entity");
      toast.success("Entity updated.");
      navigate(`/counterparties/entities/${updated.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const dedupRequest = useMemo(() => ({
    legalEntityName: form.legalEntityName || null,
    dba: form.dba || null,
    stateOfFormation: form.stateOfFormation || null,
    ein: editingEin || !editing ? form.ein ?? null : null,
  }), [form, editingEin, editing]);

  // Run the duplicate check up front. The modal is only shown when a
  // candidate match exists; with no match we create the record directly.
  const dedupPrecheck = useMutation({
    mutationFn: () => entitiesApi.dedupCheck(dedupRequest),
    onSuccess: (result) => {
      if (result.strength === "None") {
        create.mutate({ ...form, confirmCreateNew: true, createNewReason: null });
        return;
      }
      setDedupResult(result);
      setDedupOpen(true);
    },
    onError: (e: Error) => setError(e.message),
  });

  const onSave = () => {
    setError(null);
    if (!form.legalEntityName || !form.primaryEmail || !form.primaryPhone) {
      setError("Please fill all required fields (legal name, email, phone).");
      return;
    }
    if (!editing) {
      runCreate();
      return;
    }
    const changes = detectCriticalFieldChanges(existing.data, form, editingEin);
    if (changes.length > 0) {
      setConfirmCritical({
        changes,
        onConfirm: () => { setConfirmCritical(null); submitUpdate(); },
      });
      return;
    }
    submitUpdate();
  };

  const runCreate = (confirmCreateNew = false, reason: string | null = null) => {
    if (!confirmCreateNew) {
      dedupPrecheck.mutate();
      return;
    }
    create.mutate({ ...form, confirmCreateNew, createNewReason: reason });
  };

  const submitUpdate = () => {
    if (!existing.data) return;
    update.mutate({ ...form, status: existing.data.status });
  };

  const isPending = create.isPending || update.isPending || dedupPrecheck.isPending;
  const requiredComplete = !!form.legalEntityName && !!form.primaryEmail && !!form.primaryPhone;

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Entity"
        title={editing ? "Edit entity" : "New entity"}
        description={editing ? "Edit identity, contact, banking, and authorized-signer details. Critical-field changes are confirmed." : undefined}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button size="sm" onClick={onSave} loading={isPending} disabled={!requiredComplete}>
              <Save size={14} strokeWidth={1.5} aria-hidden /> {editing ? "Save changes" : "Save Entity"}
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-status-error/10 border-l-[3px] border-status-error text-small text-status-error p-space-3 rounded-md mb-space-3" role="alert">
          {error}
        </div>
      )}

      <EntityForm
        value={form}
        onChange={setForm}
        einMasked={existing.data?.einMasked}
        bankRoutingMasked={existing.data?.bankRoutingNumberMasked}
        bankAccountMasked={existing.data?.bankAccountNumberMasked}
        editingEin={editingEin}
        onEditEin={() => setEditingEin(true)}
        editingBank={editingBank}
        onEditBank={() => setEditingBank(true)}
      />

      <DedupCheckModal
        open={dedupOpen}
        kind="Entity"
        request={dedupRequest}
        result={dedupResult ?? undefined}
        onLinkExisting={(c: DedupCandidateDto) => {
          setDedupOpen(false);
          toast.info(`Linked to existing entity ${c.displayName}.`);
          navigate(`/counterparties/entities/${c.id}`);
        }}
        onCreateNew={(reason) => {
          setDedupOpen(false);
          runCreate(true, reason || null);
        }}
        onCancel={() => {
          setDedupOpen(false);
          setDedupResult(null);
        }}
      />

      {confirmCritical && (
        <CriticalFieldEditConfirmModal
          open
          changes={confirmCritical.changes}
          onConfirm={confirmCritical.onConfirm}
          onCancel={() => setConfirmCritical(null)}
        />
      )}
    </>
  );
}

function entityDetailToFormState(e: EntityDetailDto): EntityFormState {
  return {
    legalEntityName: e.legalEntityName,
    dba: e.dba ?? "",
    entityType: e.entityType,
    stateOfFormation: e.stateOfFormation ?? "",
    dateOfFormation: e.dateOfFormation ?? null,
    ein: null,
    formationStatus: e.formationStatus ?? null,
    primaryEmail: e.primaryEmail,
    primaryPhone: e.primaryPhone,
    address: e.address,
    mailingAddress: e.mailingAddress ?? null,
    accreditedInvestorStatus: e.accreditedInvestorStatus ?? null,
    accreditedInvestorBasis: e.accreditedInvestorBasis ?? null,
    taxFilingStatus: e.taxFilingStatus ?? null,
    originalSaleDate: e.originalSaleDate ?? null,
    originalSaleBasisInfo: e.originalSaleBasisInfo ?? "",
    bankACHName: e.bankACHName ?? "",
    bankRoutingNumber: null,
    bankAccountNumber: null,
    bankAccountType: e.bankAccountType ?? null,
    authorizedSignersJson: e.authorizedSignersJson ?? null,
    tags: e.tags,
  };
}

function detectCriticalFieldChanges(
  before: EntityDetailDto | undefined,
  after: EntityFormState,
  editingEin: boolean,
): CriticalFieldChange[] {
  if (!before) return [];
  const changes: CriticalFieldChange[] = [];
  if (before.legalEntityName !== after.legalEntityName) {
    changes.push({
      field: "Legal entity name",
      before: before.legalEntityName,
      after: after.legalEntityName,
    });
  }
  if (editingEin && (after.ein ?? "").length > 0) {
    changes.push({
      field: "EIN",
      before: before.einMasked ?? "—",
      after: "**-***" + (after.ein ?? "").slice(-4),
    });
  }
  if ((after.bankRoutingNumber ?? "").length > 0) {
    changes.push({
      field: "Bank routing number",
      before: before.bankRoutingNumberMasked ?? "—",
      after: "*****" + (after.bankRoutingNumber ?? "").slice(-4),
    });
  }
  if ((after.bankAccountNumber ?? "").length > 0) {
    changes.push({
      field: "Bank account number",
      before: before.bankAccountNumberMasked ?? "—",
      after: "*****" + (after.bankAccountNumber ?? "").slice(-4),
    });
  }
  return changes;
}
